// TIP-647 — the PUBLIC, company-less "Become a member" page at /membership.
// The homepage CTA, nav "Plans" and footer "Pricing" used to point at /plans,
// which is the Buy-a-Web-Profile checkout: a visitor with no company context
// hit PaymentPlans' guard, got an error toast and was dumped on /search. This
// page is the real landing spot — anyone can read the perks and pricing with no
// company and no account.
// Payment still requires a claimed company (product decision: we never take
// money and hold it against a profile nobody owns), so Proceed runs a gate
// rather than going straight to Stripe:
//   not logged in            → /login, returning here with the plan intact
//   logged in, no claim      → /search, the claim flow, with an explanation
//   logged in, >=1 claim     → company picker (ALWAYS, even for exactly one),
//                              then the existing /membership/:companyId checkout
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PaymentScreen from "../../components/public/VerifyAccount/PaymentScreen";
import { useGetIndividualQuery } from "../../store/Company/companyApiSlice";
import { setActiveCompany } from "../../store/activeCompanySlice";
import { CommonToast } from "../../components/common/Toast";
import { Modal } from "../../components/common";

export const NO_CLAIM_MESSAGE =
  "Membership attaches to a business profile you've claimed. Find your business and claim it first — it's free.";

const MembershipPlans = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, userInfo } = useSelector((state) => state.auth);

  const individualID = userInfo?.individual?.[0]?.individualID;
  // Same source and filter as the dashboard's company list, so the two agree on
  // what "my claimed businesses" means.
  const { data, isLoading, isFetching, isError } = useGetIndividualQuery(
    { individualID, token },
    { skip: !token || !individualID }
  );
  const claimedCompanies = useMemo(
    () => (data?.individual || []).filter((entry) => entry?.companyID?.claimed),
    [data]
  );

  // Non-null once Proceed has been pressed while logged in: the plan we are
  // running the gate for. Held in state rather than decided inline because the
  // company list may still be in flight when the button is pressed — deciding
  // then would read zero companies and wrongly send an owner to /search.
  const [pendingPlan, setPendingPlan] = useState(null);

  // "Not answered yet" is NOT "answered zero". An RTK Query that is SKIPPED —
  // which it is until `individualID` is known — reports isLoading:false and
  // isFetching:false with data undefined, exactly like a settled empty result.
  // Keying the decision off the loading flags alone therefore sent a genuine
  // owner to the claim flow. Only `data` actually arriving counts as an answer.
  const companiesResolved = Boolean(data) && !isLoading && !isFetching;
  const resolving = Boolean(pendingPlan) && !companiesResolved && !isError;

  useEffect(() => {
    if (!pendingPlan) return;
    if (isError) {
      // Don't guess: a failed lookup must not be read as "owns nothing" and
      // silently route the user away from checkout.
      setPendingPlan(null);
      CommonToast.error(
        "Couldn't load your businesses. Please try again."
      );
      return;
    }
    if (!companiesResolved) return;
    if (claimedCompanies.length === 0) {
      setPendingPlan(null);
      CommonToast.info(NO_CLAIM_MESSAGE);
      navigate("/search");
    }
    // With at least one claimed company the picker renders; see below.
  }, [pendingPlan, companiesResolved, isError, claimedCompanies, navigate]);

  // React Router keeps the previous page's scroll offset, so arriving from a
  // homepage CTA or the footer dropped the visitor at the price card or the
  // footer with the hero off-screen. Found in QA.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleProceed = ({ plan }) => {
    if (!token) {
      // PrivateRoute stores only `pathname + search` as lastVisited and Login
      // reads it verbatim, so the chosen plan travels in the query string;
      // router state would not survive the round trip.
      try {
        localStorage.setItem("lastVisited", `/membership?plan=${plan}`);
      } catch (err) {
        // private mode / storage disabled — the bounce still works, the user
        // just lands on the default post-login page.
      }
      navigate("/login");
      return;
    }
    setPendingPlan(plan);
  };

  const handlePick = (entry) => {
    const company = entry?.companyID;
    if (!company?.companyID) return;
    // Keep the rest of the app acting as the company they just chose.
    dispatch(setActiveCompany(company));
    const plan = pendingPlan;
    setPendingPlan(null);
    // Reuse the existing per-company checkout rather than duplicating the
    // Stripe call here — same pattern as MembershipStatusCard.
    // Carry the name so the checkout confirms WHICH business the user just
    // chose. PaymentScreen reads location.state.companyName first in its name
    // derivation, ahead of the "your business" fallback it would otherwise show
    // (that fallback is TIP-650, which still affects the other entry points).
    // Only send a real name — an empty string would render an empty bold span.
    const companyName = company.registered_company_name || undefined;
    // fromMembershipPage: the checkout was built for the verify-account flow,
    // where "Final step" is true; arriving from here it is not, so it hides it.
    navigate(`/membership/${company.companyID}`, {
      state: { plan, fromMembershipPage: true, ...(companyName && { companyName }) },
    });
  };

  const showPicker =
    Boolean(pendingPlan) && companiesResolved && claimedCompanies.length > 0;

  return (
    <>
      <PaymentScreen publicMode onProceed={handleProceed} />

      {resolving && (
        <Modal modalHandle={() => setPendingPlan(null)}>
          <div className="w-[420px] max-w-full px-6 py-8 text-center">
            <p className="text-f16 text-secondarytwo">Loading your businesses…</p>
          </div>
        </Modal>
      )}

      {showPicker && (
        <Modal modalHandle={() => setPendingPlan(null)}>
          <div className="w-[480px] max-w-full px-6 py-6">
            <h2 className="text-f20 font-bold text-primary mb-1">
              Which business is this membership for?
            </h2>
            {/* Shown even when there is exactly one: the user should always see
                and confirm which business they are buying for. */}
            <p className="text-f14 text-secondarytwo mb-5">
              Membership applies to one business profile. Pick the one you want
              to upgrade.
            </p>
            <ul className="flex flex-col gap-2 max-h-[320px] overflow-y-auto">
              {claimedCompanies.map((entry) => (
                <li key={entry.companyID.companyID}>
                  <button
                    type="button"
                    onClick={() => handlePick(entry)}
                    className="w-full text-left border border-[#E5E7EB] rounded-md px-4 py-3 hover:border-[#FB512D] hover:bg-[#FFF5F2] transition-colors"
                  >
                    <span className="block text-f15 font-semibold text-primary">
                      {entry.companyID.registered_company_name}
                    </span>
                    <span className="block text-f13 text-secondarytwo">
                      {entry.companyID.companyID}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Modal>
      )}
    </>
  );
};

export default MembershipPlans;
